import { format } from 'date-fns'
import { BusinessOpeningHour } from '@/types'

export interface SingleDayDisplayHours {
  start: number // Hour (0-23, can extend beyond for overnight)
  end: number   // Hour (1-36, max 12:00 next day for overnight)
  startTime: string // HH:MM format
  endTime: string   // HH:MM format
}

export interface Shift {
  startTime: string // HH:MM format or ISO string
  endTime: string   // HH:MM format or ISO string
  start_time?: string // Alternative property name
  end_time?: string   // Alternative property name
}

/**
 * Calculate display hours for a single day based on shifts and opening hours
 * This replaces the backend edge function with frontend-only calculation
 *
 * Uses unified calculation: takes the most expansive range
 * - Start: min(earliestShiftStart, openingHourStart) - 1hr padding
 * - End: max(latestShiftEnd, openingHourClose) + 1hr padding
 * - Fallback: 8:00-18:00 (if no shifts AND no opening hours)
 */
export function calculateSingleDayDisplayHours(
  shifts: Shift[],
  openingHours: BusinessOpeningHour[],
  date: string
): SingleDayDisplayHours {
  let shiftStart: number | null = null
  let shiftEnd: number | null = null
  let openStart: number | null = null
  let openEnd: number | null = null

  // Get shift range (earliest start, latest end)
  if (shifts.length > 0) {
    const times = shifts.map(shift => {
      const startTime = getTimeString(shift)
      const endTime = getEndTimeString(shift)
      return {
        start: parseTimeToHour(startTime),
        end: parseTimeToHour(endTime)
      }
    })
    shiftStart = Math.min(...times.map(t => t.start))
    shiftEnd = Math.max(...times.map(t => t.end))
  }

  // Get opening hours for the day (FIX: parse date parts to avoid UTC timezone issues)
  if (openingHours.length > 0) {
    // Parse date string parts directly to create local date (avoids UTC conversion bug)
    const [year, month, day] = date.split('-').map(Number)
    const localDate = new Date(year, month - 1, day)
    const dayOfWeek = format(localDate, 'EEEE') // "Monday", "Tuesday", etc.
    const dayHours = openingHours.find(oh => oh.day_of_week === dayOfWeek)

    if (dayHours && !dayHours.is_closed_all_day && dayHours.opening_time && dayHours.closing_time) {
      openStart = parseTimeToHour(dayHours.opening_time)
      openEnd = parseTimeToHour(dayHours.closing_time)
    }
  }

  // Calculate unified range (most expansive)
  let displayStart: number
  let displayEnd: number

  if (shiftStart !== null && openStart !== null) {
    // Both available: take most expansive range
    displayStart = Math.min(shiftStart, openStart)
    displayEnd = Math.max(shiftEnd!, openEnd!)
  } else if (shiftStart !== null) {
    // Only shifts available
    displayStart = shiftStart
    displayEnd = shiftEnd!
  } else if (openStart !== null) {
    // Only opening hours available
    displayStart = openStart
    displayEnd = openEnd!
  } else {
    // Fallback to business hours (8:00-18:00)
    return {
      start: 8,
      end: 18,
      startTime: '08:00',
      endTime: '18:00'
    }
  }

  // Add 1-hour padding
  const paddedStart = Math.max(0, displayStart - 1)
  const paddedEnd = Math.min(36, displayEnd + 1) // Cap at 36 (12:00 next day)

  return {
    start: paddedStart,
    end: paddedEnd,
    startTime: formatHour(paddedStart),
    endTime: formatHour(paddedEnd)
  }
}

/**
 * Get start time string from shift object (handles different property names)
 */
function getTimeString(shift: Shift): string {
  return shift.startTime || shift.start_time || '00:00'
}

/**
 * Get end time string from shift object (handles different property names)
 */
function getEndTimeString(shift: Shift): string {
  return shift.endTime || shift.end_time || '23:59'
}

/**
 * Parse time string to hour number
 * Handles both "HH:MM" format and ISO datetime strings
 */
function parseTimeToHour(timeStr: string): number {
  if (!timeStr) return 0

  // Handle ISO datetime strings (e.g., "2023-09-03T07:00:00.000Z")
  if (timeStr.includes('T')) {
    const date = new Date(timeStr)
    return date.getHours()
  }

  // Handle local datetime strings (e.g., "2023-09-03 09:00:00")
  if (timeStr.includes(' ')) {
    const timePart = timeStr.split(' ')[1]
    const [hour] = timePart.split(':').map(Number)
    return hour
  }

  // Handle time only strings (e.g., "09:00")
  const [hour] = timeStr.split(':').map(Number)
  return isNaN(hour) ? 0 : hour
}

/**
 * Format hour number to HH:MM string
 */
function formatHour(hour: number): string {
  // Handle extended hours (24+) for overnight scenarios
  const displayHour = Math.max(0, hour)
  // Cap at 36 hours (12:00 next day)
  const cappedHour = Math.min(displayHour, 36)

  if (cappedHour === 24) {
    return '24:00' // Keep 24:00 for midnight
  } else if (cappedHour > 24) {
    // Next day format: 25 -> 01:00, 26 -> 02:00, etc.
    const nextDayHour = cappedHour - 24
    return `${nextDayHour.toString().padStart(2, '0')}:00`
  }

  return `${cappedHour.toString().padStart(2, '0')}:00`
}